import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/authStore.js'
import { useChangePasswordStore } from '@/stores/changePasswordStore.js'
import { useUserStore } from '@/stores/userStore'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { useCookies } from '@vueuse/integrations/useCookies'

// Root views
import WelcomeView from '@/views/WelcomeView.vue'
import ProfileView from '@/views/ProfileView.vue'
import InvitationsView from '@/views/InvitationsView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import LoginView from '@/views/LoginView.vue'
import ArtistConfirmationView from '@/views/ArtistConfirmationView.vue'
import CreateStageView from '@/views/CreateStageView.vue'
import SsoView from '@/views/SsoView.vue'

// Onboarding views
import OnboardingView from '@/views/onboarding/OnboardingView.vue'
import OnboardingStageView from '@/views/onboarding/OnboardingStageView.vue'
import OnboardingAccountView from '@/views/onboarding/OnboardingAccountView.vue'
import OnboardingFinishView from '@/views/onboarding/OnboardingFinishView.vue'
import RegisterView from '@/views/RegisterView.vue'

// Password Reset views
import PasswordResetView from '@/views/password-reset/PasswordResetView.vue'
import PasswordResetEmailView from '@/views/password-reset/PasswordResetEmailView.vue'
import PasswordResetCheckEmailView from '@/views/password-reset/PasswordResetCheckEmailView.vue'
import PasswordResetSuccessView from '@/views/password-reset/PasswordResetSuccessView.vue'
import PasswordResetNewView from '@/views/password-reset/PasswordResetNewView.vue'
import RegisterSuccessView from '@/views/RegisterSuccessView.vue'
import ChangePasswordView from '@/views/ChangePasswordView.vue'

const routes = [
  {
    path: '/',
    redirect: () => ({ name: 'Login' })
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: { avoidAuthUser: true }
  },
  {
    path: '/onboarding',
    name: 'OnboardingView',
    redirect: '/onboarding/stage',
    component: OnboardingView,
    meta: { avoidAuthUser: true },
    children: [
      {
        name: 'StageOnboarding',
        path: 'stage',
        component: OnboardingStageView
      },
      {
        name: 'AccountOnboarding',
        path: 'account',
        component: OnboardingAccountView,
        meta: { requiredCountryCheck: true }
      }
    ]
  },
  {
    path: '/register',
    name: 'RegisterView',
    component: RegisterView,
    meta: { avoidAuthUser: true }
  },
  {
    path: '/register/checkmail',
    name: 'RegisterSuccess',
    component: RegisterSuccessView,
    meta: { requiredSentOnboardingSuccess: true, avoidAuthUser: true }
  },
  {
    name: 'OnboardingFinishView',
    path: '/onboarding/next-steps',
    component: OnboardingFinishView
  },
  {
    path: '/password',
    name: 'PasswordResetView',
    redirect: { name: 'PasswordResetEmailView' },
    component: PasswordResetView,
    meta: { avoidAuthUser: true },
    children: [
      {
        name: 'PasswordResetEmailView',
        path: 'enter-email',
        component: PasswordResetEmailView
      },
      {
        name: 'NextStepsPasswordReset',
        path: 'next-steps',
        component: PasswordResetCheckEmailView,
        meta: { hasSentLinkSuccess: true }
      },
      {
        name: 'PasswordResetSuccessView',
        path: 'success',
        component: PasswordResetSuccessView,
        meta: { hasChangedPasswordSuccess: true }
      },
      {
        name: 'PasswordResetNewView',
        path: 'reset/:token',
        props: true,
        component: PasswordResetNewView
      }
    ]
  },
  {
    name: 'ArtistConfirmationView',
    path: '/artists/:emailConfirmationToken/confirm',
    props: true,
    component: ArtistConfirmationView,
    meta: { avoidAuthUser: true }
  },
  {
    path: '/welcome',
    name: 'WelcomeView',
    component: WelcomeView,
    meta: { requiredAuth: true }
  },
  {
    path: '/profile',
    name: 'ProfileView',
    component: ProfileView,
    meta: { requiredAuth: true }
  },
  {
    path: '/profile/change-password',
    name: 'ChangePasswordView',
    component: ChangePasswordView,
    meta: { requiredAuth: true }
  },
  {
    path: '/invitations',
    name: 'InvitationsView',
    component: InvitationsView,
    meta: { requiredAuth: true }
  },
  {
    path: '/create-stage',
    name: 'CreateStageView',
    component: CreateStageView,
    meta: { requiredAuth: true }
  },
  {
    path: '/sso',
    name: 'SsoView',
    component: SsoView,
    meta: { requiredAuth: true }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFoundView',
    component: NotFoundView
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  }
})

router.beforeEach(async (to, from, next) => {
  /***********************
	// AUTHENTICATION
	************************/

  const authStore = useAuthStore()
  const userStore = useUserStore()

  const $cookies = useCookies(['refresh_token'])
  if (!authStore.authData.token) {
    if (localStorage.getItem('refresh_token')) {
      await authStore.refreshToken(localStorage.getItem('refresh_token'))
    } else if ($cookies.get('refresh_token')) {
      await authStore.refreshToken($cookies.get('refresh_token'))
    }
  }

  const isTokenActive = authStore.isTokenActive

  const { redirectPath, ...additionalQuery } = to.query

  if (isTokenActive && redirectPath) {
    const formattedQuery = additionalQuery || {}

    return next({ path: redirectPath, query: { ...formattedQuery } })
  } else if (isTokenActive && to.meta.requiredAuth) {
    await userStore.getUserData()
    return next()
  } else if (isTokenActive && to.meta.avoidAuthUser) {
    return next({ name: 'WelcomeView' })
  } else if (!isTokenActive && to.meta.requiredAuth) {
    await authStore.refreshToken(localStorage.getItem('refresh_token'))
    await userStore.getUserData()
    return next()
  }

  /***********************
	// ONBOARDING
	************************/
  const onboardingStore = useOnboardingStore()

  if (to.meta.requiredCountryCheck) {
    if (onboardingStore.hasAccessToSecondStep) {
      return next()
    } else {
      return next({ name: 'StageOnboarding' })
    }
  }

  if (to.meta.requiredSentOnboardingSuccess) {
    if (onboardingStore.isOnboardingSuccess) {
      return next()
    } else {
      return next({ name: 'StageOnboarding' })
    }
  }

  /***********************
	// PASSWORD RESET
	************************/
  const changePasswordStore = useChangePasswordStore()
  if (to.meta.hasSentLinkSuccess) {
    if (changePasswordStore.hasSentLinkSuccess) {
      return next()
    } else {
      return next({ name: 'PasswordResetView' })
    }
  }

  if (to.meta.hasChangedPasswordSuccess) {
    if (changePasswordStore.hasChangedPasswordSuccess) {
      return next()
    } else {
      return next({ name: 'PasswordResetView' })
    }
  }
  /**********************************************
	// CONTINUE TO VIEW IF NONE OF THE ABOVE APPLY
	***********************************************/
  return next()
})

export default router
